/**
 * About.js
 *
 * About page providing information about the Gifted platform.
 *
 * Route: /about
 */

import React from 'react';
import { PAYPAL_PERCENTAGE_FEE, PAYPAL_FIXED_FEE } from './utils/constants';
import Navigation from './components/Navigation';
import { useAuth } from './AuthContext';

function About() {
  const { isAuthenticated } = useAuth();
  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="mb-10">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">About Gifted</h1>
          <p className="text-gray-500 text-sm">Where generosity builds connections</p>
        </div>

        <div className="space-y-10 text-gray-700 text-sm sm:text-base leading-relaxed">

          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">What it is</h2>
            <p className="mb-3">
              Gifted lets you create a gift pool - a shared fund - for yourself or for someone you care about.
              Friends, community members, or anyone who comes across the pool can contribute. Unlike traditional
              campaign-driven crowdfunding platforms, Gifted is designed more like a feed - you can follow
              people, see when they share or update a pool, and build ongoing connections through
              generosity, not just a single campaign moment.
            </p>
            <p>
              It's not just for moments of need either - it's also for celebrations: a honeymoon fund, a group
              gift, a new baby, a creative project. Bringing these moments together is intentional: when
              needs and celebrations share the same space, asking and giving both feel more normal, not exceptional,
              making support feel like a natural part of everyday life that flows freely across all of life's moments.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">The problem Gifted is trying to solve</h2>
            <p>
              Today, a lot of peer support is scattered across Venmo requests, bios,
              and direct messages. It works, but it's fragmented and hard to track. If you want to
              help or celebrate someone, you usually have to already know their story. Gifted tries to make that
              easier: a single place where people can share what they need and where people who want
              to give can actually find them  - in a way that feels simple, secure, and judgment-free.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">How funds are handled</h2>
            <p className="mb-3">
              All gifts are processed through PayPal's multiparty integration. As an approved PayPal
              Partner, Gifted passed a rigorous review process to ensure our platform meets their
              standards for security and fund management.
            </p>
            <p>
              Gifted does not hold, manage, or have access to anyone's money at any point. Funds move
              directly between the gifter and the recipient's PayPal account.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Fees</h2>
            <p className="mb-3">
              Gifted is built so that every dollar you give goes to the recipient - no platform
              fees are deducted from gifts.
            </p>
            <p className="mb-3">
              All gifts are processed in US dollars (USD). If you're paying from a non-US account,
              PayPal will convert your local currency at checkout.
            </p>
            <p className="mb-3">
              PayPal charges a standard processing fee of {(PAYPAL_PERCENTAGE_FEE * 100).toFixed(1)}% + ${PAYPAL_FIXED_FEE} per transaction, which is
              added to the gifter's total and shown upfront at checkout so there are no surprises.
              This ensures that recipients always receive the full amount intended by the gifter.
            </p>
            <p>
              Gifters also have the option to add a small gift to the platform at checkout. This
              support is completely optional and helps us maintain a free and safe space for generosity
              to thrive.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Trust and safety</h2>
            <p className="mb-3">
              Gift is powered by a social graph, allowing trust signals to emerge naturally.
              By seeing mutual connections, community vouches, and gifting activity,
              we provide the transparency and meaningful context you need to give and receive with peace of mind.
            </p>
            <p className="mb-3">
              Every gift pool also undergoes a brief review before disbursement - up to three days.
              During this time, PayPal holds the funds while Gifted monitors each pool for any red
              flags before releasing them. This process works alongside PayPal's own identity
              verification and fraud protection measures to ensure every gift is secure.
            </p>
            <p className="mb-3">
              Users can report any gift pool they have concerns about. We're actively thinking about
              additional safeguards as the platform grows, and want to learn from you regarding
              what actually matters to you.
            </p>
            <p className="mb-3">
              Please reach out with any thoughts or feedback - we're all ears!
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Frequently asked questions</h2>
            <p>
              Answers to common questions about giving, receiving, and PayPal onboarding are on our{' '}
              <a href="/faq" className="text-indigo-600 hover:text-indigo-700 underline">
                FAQ page
              </a>
              .
            </p>
          </section>

        </div>

        {!isAuthenticated && (
          <div className="mt-12 pt-8 border-t border-gray-200 flex gap-4 flex-wrap">
            <a
              href="/signup"
              className="px-6 py-2.5 bg-indigo-500 text-white rounded-lg font-medium hover:bg-indigo-600 transition-colors text-sm"
            >
              Create an account
            </a>
            <a
              href="/"
              className="px-6 py-2.5 bg-white text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors border border-gray-300 text-sm"
            >
              Browse the feed
            </a>
          </div>
        )}

        <div className="mt-8 text-sm text-gray-500">
          Questions or feedback?{' '}
          <a href="mailto:giftedbeta1@gmail.com" className="text-indigo-600 hover:text-indigo-700 underline">
            giftedbeta1@gmail.com
          </a>
        </div>
      </div>
    </div>
  );
}

export default About;
